import React from 'react';
import { Link } from 'react-router-dom';

/**
 * "Who it's for" — the three-cell audience band from ShiftWagon Redesign v2.
 *
 * The middle cell is asphalt so the row reads as one object rather than three
 * floating cards; `sw-on-dark` flips the eyebrow and action link to the tokens
 * that stay legible there. Copy is verbatim from the design.
 */

interface Audience {
  kicker: string;
  title: string;
  copy: string;
  action: string;
  to: string;
  /** The asphalt cell. Exactly one, per the design. */
  dark?: boolean;
  id?: string;
}

const AUDIENCES: Audience[] = [
  {
    kicker: 'For people',
    title: 'Move a couch, a fridge, a whole room.',
    copy: 'Start with a route estimate, then confirm the final fare before dispatch.',
    action: 'Get an estimate',
    to: '/#estimate',
    /* The footer's "Home Moves" / "Retail Deliveries" links land here. */
    id: 'solutions',
  },
  {
    kicker: 'For business',
    title: 'Add truck capacity when the day gets busy.',
    copy: 'Store runs, deliveries and overflow work on a simpler path.',
    action: 'Talk to us',
    to: '/about#contact',
    dark: true,
  },
  {
    kicker: 'For drivers',
    title: 'Own a bakkie? Turn it into work.',
    copy: 'Team-guided onboarding for selected operators and fleet owners.',
    action: 'Apply to drive',
    to: '/about#contact',
    id: 'drivers',
  },
];

const WhoItsForSection: React.FC = () => {
  return (
    <section className="sw-section" id="business">
      <div className="container">
        <div className="sw-who-head">
          <div>
            <span className="sw-eyebrow">Who it&apos;s for</span>
            <h2>
              One booking flow,
              <br />
              three kinds of move.
            </h2>
          </div>
          <p className="sw-who-lede">
            Start from the role that matches what you need — move something, add delivery
            capacity, or drive.
          </p>
        </div>

        <div className="sw-who-grid">
          {AUDIENCES.map((audience) => (
            <div
              className={`sw-who-cell${audience.dark ? ' sw-who-cell-dark sw-on-dark' : ''}`}
              key={audience.kicker}
              id={audience.id}
            >
              <span className="sw-who-kicker">{audience.kicker}</span>
              <h3 className="sw-who-title">{audience.title}</h3>
              <p className="sw-who-copy">{audience.copy}</p>
              <Link className="sw-link-action" to={audience.to}>
                {audience.action} <span aria-hidden="true">&rarr;</span>
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhoItsForSection;
